import React, { useContext, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import LoadingPage from '@vimeo/core/components/LoadingPage';
import { useQuery } from '@vimeo/core/hooks/use-api-resource';
import { themes as themes8 } from '@vimeo/iris/themes';
import { AppLayout, FooterOption, HeaderOption } from '@vimeo/layouts/AppLayout';
import { Viewer } from '@vimeo/viewer';

import SiteWrapper from './components/SiteWrapper/SiteWrapper';
import AlbumConfigContext from './containers/AlbumConfigContext';
import AlbumsApp from './containers/AlbumsApp';
import ErrorNotifier from './containers/ErrorNotifier';
import { EmbedShowcaseContext } from './context/useEmbedShowcase';
import { VimeoAlbumInfoResponse } from './types';
import { albumRequestFields, LAYOUT_PLAYER, LAYOUT_PLAYLIST } from './utils/constants';
import { hasPreviewParam, parseQuery } from './utils/helpers';

const emptyAlbum: any = {};

interface Params {
  albumId: string;
}

interface Props extends RouteComponentProps<Params> {
  id: number;
  albumHash?: string;
  viewer: Viewer;
  hashedPass: string;
}

export function Showcase({ id, albumHash, viewer, match, hashedPass }: Props) {
  const embedShowcaseData = useContext(EmbedShowcaseContext);

  if (!match || !viewer) {
    return null;
  }

  const { user, vimeoHttpsUrl } = viewer;

  const { data: album, error } = useQuery<VimeoAlbumInfoResponse>(`/albums/${id}`, {
    variables: {
      fields: albumRequestFields,
      preview: hasPreviewParam(),
      referrer: embedShowcaseData?.referrer,
      is_embed: embedShowcaseData?.isShowcaseEmbeded,
    },
  });

  // Only owners and admins get embed property, so use as proxy for `isOwnerOrAdmin`, for now
  const isOwnerOrAdmin = !!album?.embed;
  const navIsHidden: boolean = Boolean(album && album.hideNav);
  // TODO: window reference will break with SSR. Need alternative for prerendering.
  const isOffsite = album ? window.location.hostname === album.domain : undefined;

  const config = useMemo(() => {
    return {
      hashedPass,
      isOffsite,
      isOwnerOrAdmin,
      albumId: id,
      navIsHidden: navIsHidden,
    };
  }, [viewer, isOwnerOrAdmin, hashedPass, navIsHidden, isOffsite]);

  if (error) {
    throw error;
  }

  if (!album) {
    return <LoadingPage />;
  }

  const { brandColor, hideNav, theme, layout, webBrandColor } = album;

  /**
   * Do not show vimeo navigation for any of the following cases -
   * 1. Viewing showcase on offsite
   * 2. Viewing showcase on embed page
   * 3. User has disabled vimeo navigation for this showcase
   * 4. Showcase layout type is playlist
   */
  const hideVimeoNavigation =
    isOffsite || embedShowcaseData?.isShowcaseEmbeded || hideNav || layout === LAYOUT_PLAYLIST;

  // If there's a clip ID in the URL and the layout is not live go the clip page.
  const clipId = parseQuery(location.search).video;
  if (clipId && clipId.length > 0 && layout !== 'live') {
    location.href = `${vimeoHttpsUrl}/showcase/${id}/video/${clipId}`;
    // Shows loading page until new location resolves;
    return <LoadingPage />;
  }

  const layoutOptions = {
    header: hideVimeoNavigation ? HeaderOption.none : HeaderOption.default,
    footer: hideVimeoNavigation ? FooterOption.none : FooterOption.minimal,
  };

  // If page is in URL and layout isn't 'player' use it as default page state
  const defaultPage =
    album.layout !== LAYOUT_PLAYER ? Number(parseQuery(location.search).page) || 1 : 1;

  // Convert the Album theme to Iris format:
  const irisTheme = theme === 'standard' ? 'light' : 'dark';

  return (
    <AlbumConfigContext.Provider value={config}>
      <ErrorNotifier>
        {({ handleError }) => (
          <ThemeProvider theme={themes8[irisTheme]}>
            <AppLayout viewer={viewer} options={layoutOptions} hasThemeSupport={false}>
              <SiteWrapper
                color={brandColor}
                hideNav={hideVimeoNavigation}
                layout={layout}
                theme={theme}
                webBrandColor={webBrandColor}
              >
                {album === emptyAlbum ? (
                  <LoadingPage />
                ) : (
                  <AlbumsApp
                    albumId={id}
                    albumHash={albumHash}
                    handleError={handleError}
                    curUser={user || undefined} // preferring undefined to null here
                    album={album}
                    hashedPass={hashedPass}
                    defaultPage={defaultPage}
                  />
                )}
              </SiteWrapper>
            </AppLayout>
          </ThemeProvider>
        )}
      </ErrorNotifier>
    </AlbumConfigContext.Provider>
  );
}
